import round from 'lodash/round';
import type {Series} from 'highcharts';

import {TooltipBulletStyle, TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {IChartDataItem} from 'common/charts/visualization/line-chart/types';
import {formatDate} from 'common/util/date';
import {commaizeNumber, htmlEncodeString} from 'common/util';
import {formatTrendValue, getFunnelTrendValueGetter, TrendValueFormat} from 'common/report/funnels/models/trends';
import {
  BasicFunnelStep,
  ColorChoice,
  DateKeyedFunnelSegments,
  TrendChartType,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getRenamedOrderedSegmentSteps} from 'common/report/funnels/models/utils';
import {LineStyle} from 'common/charts/visualization/line-chart/enums';
import {TrendDataRow, TrendDatePoint} from 'common/components/data-table/trend-data/types';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {LiftType} from 'common/report/util/lift/types';
import {THEME} from 'common/components/text-highlight';
import {SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {HIGHCHARTS_LINKED_ID_SUFFIX} from 'common/report/constants';

function getStepLabel(step: BasicFunnelStep): string {
  return step.step_label || step.event;
}

export function formatYAxisLabel(value: number, trendType: TrendChartType, liftType?: LiftType): string {
  return formatTrendValue({value, trendChartType: trendType, formatType: TrendValueFormat.ChartAxis, liftType});
}

export function formatTooltip({
  x,
  series,
  comparedTrends,
  liftTrends,
  eventIndex,
  funnelsQuery,
  orderedTrends,
  trendType,
}: {
  x: number;
  series: Series;
  comparedTrends: DateKeyedFunnelSegments[];
  liftTrends: DateKeyedFunnelSegments[];
  eventIndex: null | number;
  funnelsQuery: FunnelsQuery;
  isCompact: boolean;
  orderedTrends: DateKeyedFunnelSegments[];
  trendType: TrendChartType;
}): string {
  const seriesProp = series.options.custom.prop;
  const matchingTrendIndex = orderedTrends.findIndex((bucket) => x === bucket.date.getTime());
  const matchingTrend = orderedTrends[matchingTrendIndex].segments.find((segment) => seriesProp === segment.prop);
  const displayName = matchingTrend.propName;
  let compareSteps;
  let comparisonDate;
  if (comparedTrends?.length > 0) {
    compareSteps = comparedTrends[matchingTrendIndex].segments.find((segment) => seriesProp === segment.prop).steps;
    comparisonDate = comparedTrends[matchingTrendIndex].date.getTime();
  }

  let liftSteps, liftComparisonSegment, liftSegmentName;
  if (liftTrends?.length > 0) {
    liftSteps = liftTrends[matchingTrendIndex].segments.find((segment) => seriesProp === segment.prop).steps;

    // if no date comparison, this is baseline compare and we need extra info for the tooltip
    if (!compareSteps) {
      liftSegmentName = funnelsQuery.liftComparison?.groupValue;
      const baselinePath = matchingTrend.props.map((prop) => prop.prop).slice(0, matchingTrend.props.length - 1);
      baselinePath.push(liftSegmentName);
      const lookupKey = baselinePath.join(SEGMENT_SEPARATOR);
      liftComparisonSegment = orderedTrends[matchingTrendIndex].segments.find((segment) => lookupKey === segment.prop)
        .steps;
    }
  }
  const color = series.options.color as string;
  const toolTipSections = getToolTipSections({
    date: orderedTrends[matchingTrendIndex].date.getTime(),
    displayName,
    color,
    trendUnit: funnelsQuery.trendUnit,
    trendTypeSelection: trendType,
    eventIndex,
    steps: matchingTrend.steps,
    compareSteps,
    comparisonDate,
    liftSteps,
    liftSegmentName,
    liftComparisonSegment,
  });
  let toolTipAttrs = `sections="${htmlEncodeString(JSON.stringify(toolTipSections))}"`;

  if (!compareSteps) {
    toolTipAttrs = `title-color="${color}" ${toolTipAttrs}`;
  }

  return `
  <mp-chart-tooltip ${toolTipAttrs}>
`;
}

/** get tooltip sections based on if there is comparison values or not */
export function getToolTipSections({
  date,
  displayName,
  color,
  trendUnit,
  trendTypeSelection,
  eventIndex,
  steps,
  compareSteps,
  comparisonDate,
  liftSteps,
  liftSegmentName,
  liftComparisonSegment,
}: {
  date: number;
  displayName: string;
  color: string;
  trendUnit: TrendUnit;
  trendTypeSelection: TrendChartType;
  eventIndex: number;
  steps: BasicFunnelStep[];
  compareSteps?: BasicFunnelStep[];
  comparisonDate?: number;
  liftSteps?: BasicFunnelStep[];
  liftSegmentName?: string;
  liftComparisonSegment: BasicFunnelStep[];
}): TooltipSection[] {
  // Highcharts callbacks don't work with arrow functions
  const formattedDate = formatDate(date, {unit: trendUnit});
  let sections: TooltipSection[] = [];
  if (liftSteps && compareSteps) {
    sections = getLiftCompareToPastTooltipSections({
      trendTypeSelection,
      liftSteps,
      eventIndex,
      displayName,
      steps,
      formattedDate,
      compareSteps,
      comparisonDate,
      trendUnit,
    });
  } else if (liftSteps) {
    sections = getLiftBaselineTooltipSections({
      trendTypeSelection,
      liftSteps,
      eventIndex,
      displayName,
      liftSegmentName,
      formattedDate,
      steps,
      liftComparisonSegment,
    });
  } else if (compareSteps) {
    sections = [
      {
        rows: [
          {
            items: [
              {
                text: displayName,
                type: TooltipItemType.Strong,
              },
            ],
          },
        ],
      },
      {
        bulletStyle: TooltipBulletStyle.Solid,
        bulletColor: color,
        rows: getToolTipDataRows(steps, eventIndex, trendTypeSelection, formattedDate),
      },
      {}, // spacing
      {
        bulletStyle: TooltipBulletStyle.Dash,
        bulletColor: color,
        rows: getToolTipDataRows(
          compareSteps,
          eventIndex,
          trendTypeSelection,
          formatDate(comparisonDate, {unit: trendUnit}),
        ),
      },
    ];
  } else {
    sections = [
      {
        rows: [
          {
            items: [
              {
                text: displayName,
                type: TooltipItemType.Strong,
              },
            ],
          },
          {
            items: [
              {
                text: formattedDate,
                type: TooltipItemType.Detail,
              },
            ],
          },
        ],
      },
      {rows: getToolTipDataRows(steps, eventIndex, trendTypeSelection)},
    ];
  }

  return sections;
}

/** returns data values per tooltip based on trendType. Also formats dates if included */
function getToolTipDataRows(
  steps: BasicFunnelStep[],
  eventIndex: null | number,
  trendTypeSelection: TrendChartType,
  formattedDate: string = null,
  includeAbsoluteValue = true,
  segmentName: string = null,
) {
  const rows = [];
  const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
  const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
  const absoluteValue = step.count;
  const trendsValue = trendValueGetter.getTrendValue(step, eventIndex);
  const formattedTrendsValue = formatTrendValue({value: trendsValue, trendChartType: trendTypeSelection});
  if (formattedDate) {
    rows.push({
      items: [
        {
          text: formattedDate,
          type: TooltipItemType.Detail,
        },
      ],
    });
  }
  if (segmentName) {
    rows.push({
      items: [
        {
          text: segmentName,
          type: TooltipItemType.Strong,
        },
      ],
    });
  }
  switch (trendTypeSelection) {
    case TrendChartType.Conversion:
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `conversion`, type: TooltipItemType.Text},
        ],
      });
      break;
    case TrendChartType.TimeToConvert:
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `average time to convert`, type: TooltipItemType.Text},
        ],
      });

      break;
    case TrendChartType.TopOfFunnel: {
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `entered`, type: TooltipItemType.Text},
          {text: getStepLabel(step), type: TooltipItemType.Data},
        ],
      });

      break;
    }
    case TrendChartType.BottomOfFunnel: {
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `reached`, type: TooltipItemType.Text},
          {text: getStepLabel(step), type: TooltipItemType.Data},
        ],
      });

      break;
    }
  }
  if (
    includeAbsoluteValue &&
    trendTypeSelection !== TrendChartType.TopOfFunnel &&
    trendTypeSelection !== TrendChartType.BottomOfFunnel
  ) {
    rows.push({
      items: [
        {text: `${commaizeNumber(absoluteValue)}`, type: TooltipItemType.Data},
        {text: `total conversions`, type: TooltipItemType.Text},
      ],
    });
  }
  return rows;
}

export function getTrendDataRows({
  liftComparedTrends,
  comparedTrends,
  eventIndex,
  orderedTrends,
  selected,
  trendType,
}: {
  comparedTrends: DateKeyedFunnelSegments[];
  liftComparedTrends: DateKeyedFunnelSegments[];
  eventIndex: number;
  orderedTrends: DateKeyedFunnelSegments[];
  selected: ColorChoice[];
  trendType: TrendChartType;
  liftType?: LiftType;
}): TrendDataRow[] {
  function getSegmentData({trends, prop}: {trends: DateKeyedFunnelSegments[]; prop?: string}): TrendDatePoint[] {
    return trends.map(({date, segments}) => {
      const timestamp = date.getTime();
      const steps = segments.find((segment) => segment.prop === prop).steps;
      const trendValueGetter = getFunnelTrendValueGetter(trendType);
      const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
      const value = trendValueGetter.getTrendValue(step, eventIndex);
      return {timestamp, value};
    });
  }

  const hasTimeComparison = comparedTrends?.length > 0;
  const hasLiftComparison = liftComparedTrends?.length > 0;

  const selectedMap = new Map(selected.map(({key, color}) => [key, color]));
  return orderedTrends[0].segments.map(({prop, propName, props, steps}) => {
    const data = getSegmentData({trends: orderedTrends, prop});

    let timeComparisonData = null;
    let liftComparisonData = null;

    // Only show lift if it exists, not both lift and time
    if (hasLiftComparison) {
      liftComparisonData = getSegmentData({
        trends: liftComparedTrends,
        prop,
      });
    }
    if (hasTimeComparison) {
      timeComparisonData = getSegmentData({trends: comparedTrends, prop});
    }

    const trendValueGetter = getFunnelTrendValueGetter(TrendChartType.TopOfFunnel);
    const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
    const cohortSize = trendValueGetter.getTrendValue(step, eventIndex);
    return {
      rowId: prop,
      selectionColor: selectedMap.get(prop),
      isSelected: selectedMap.has(prop),
      segmentValues: props.length > 1 ? props.map((prop) => prop.propName ?? prop.prop) : [propName],
      data,
      timeComparisonData,
      liftComparisonData,
      cohortSize,
    };
  });
}

export function getChartData({
  comparedTrends,
  eventIndex,
  funnelsQuery,
  orderedTrends,
  segmentFilterController,
  trendType,
  liftComparisonTrends,
}: {
  comparedTrends: DateKeyedFunnelSegments[];
  eventIndex: null | number;
  funnelsQuery: FunnelsQuery;
  orderedTrends: DateKeyedFunnelSegments[];
  segmentFilterController: SegmentFilterController;
  trendType: TrendChartType;
  liftComparisonTrends: DateKeyedFunnelSegments[];
}): IChartDataItem[] {
  const segments = getRenamedOrderedSegmentSteps(funnelsQuery, orderedTrends);

  const selectedSeriesColors = new Map(
    segments
      .filter((s) => segmentFilterController.isItemSelected(0, s.prop))
      .map((s) => [s.prop, segmentFilterController.getItemColor(0, s.prop)]),
  );

  // for lift we only show the lift change, so we don't need the other trend lines
  if (liftComparisonTrends) {
    return _getChartData({
      eventIndex,
      orderedTrends: liftComparisonTrends,
      selectedSeriesColors,
      trendTypeSelection: trendType,
    });
  }

  const chartData = _getChartData({
    eventIndex,
    orderedTrends,
    selectedSeriesColors,
    trendTypeSelection: trendType,
  });

  let compareData = [];
  if (comparedTrends?.length > 0) {
    // we want to maintain the original x cooridinates so we have to replace the comparison dates before feeding to chart data
    const comparedSegments = comparedTrends.map((segment, index) => {
      return {date: orderedTrends[index].date, segments: segment.segments};
    });
    compareData = _getChartData({
      eventIndex,
      orderedTrends: comparedSegments,
      selectedSeriesColors,
      trendTypeSelection: trendType,
      isCompareData: true,
    });
  }
  return [...chartData, ...compareData];
}

function _getChartData({
  eventIndex,
  selectedSeriesColors,
  orderedTrends,
  trendTypeSelection,
  isCompareData,
}: {
  eventIndex: null | number;
  selectedSeriesColors: Map<string, string>;
  orderedTrends: DateKeyedFunnelSegments[];
  trendTypeSelection: TrendChartType;
  isCompareData?: boolean;
}): IChartDataItem[] {
  const dashStyle = isCompareData ? LineStyle.Comparison : undefined;
  const idSuffix = isCompareData ? HIGHCHARTS_LINKED_ID_SUFFIX : ``;
  return orderedTrends[0].segments.reduce((selectedSegments, {prop, propName}) => {
    if (!selectedSeriesColors.has(prop)) {
      return selectedSegments;
    }
    const segmentData: Array<[number, number]> = orderedTrends.map(({date, segments}) => {
      const timestamp = date.getTime();
      const steps = segments.find((segment) => segment.prop === prop).steps;
      const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
      const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
      return [timestamp, trendValueGetter.getTrendValue(step, eventIndex)];
    });
    const linkedTo = isCompareData ? prop : undefined;
    selectedSegments.push({
      color: selectedSeriesColors.get(prop),
      name: propName,
      id: prop + idSuffix,
      linkedTo,
      label: prop,
      data: segmentData,
      dashStyle,
      visible: true,
    });
    return selectedSegments;
  }, [] as IChartDataItem[]);
}

/** Lift compare to past, format is
 * **name** (bold)
 * lift% (data)
 * XX% (data) originaldate
 * XX% (data) compare date
 */
function getLiftCompareToPastTooltipSections({
  trendTypeSelection,
  liftSteps,
  eventIndex,
  displayName,
  steps,
  formattedDate,
  compareSteps,
  comparisonDate,
  trendUnit,
}: {
  trendTypeSelection: TrendChartType;
  liftSteps: BasicFunnelStep[];
  eventIndex: number;
  displayName: string;
  steps: BasicFunnelStep[];
  formattedDate: string;
  compareSteps: BasicFunnelStep[];
  comparisonDate: number;
  trendUnit: TrendUnit;
}) {
  const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
  const step = trendValueGetter.getStep(liftSteps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
  const liftTrendValue = trendValueGetter.getTrendValue(
    step,
    Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex,
  );
  const liftDisplayValue = `${liftTrendValue > 0 ? `+` : ``}${round(liftTrendValue * 100, 4)}%`;
  return [
    {
      rows: [
        {
          items: [
            {
              text: displayName,
              type: TooltipItemType.Strong,
            },
          ],
        },
      ],
    },
    {
      rows: [
        {
          items: [
            {
              text: liftDisplayValue,
              type: TooltipItemType.Data,
              theme: liftTrendValue < 0 ? THEME.NEGATIVE : THEME.POSITIVE,
            },
          ],
        },
      ],
    },
    {
      rows: getToolTipDataRows(steps, eventIndex, trendTypeSelection, formattedDate, null),
    },
    {
      rows: getToolTipDataRows(
        compareSteps,
        eventIndex,
        trendTypeSelection,
        formatDate(comparisonDate, {unit: trendUnit}),
        null,
      ),
    },
  ];
}

/** Lift baseline tooltip format is
 * name
 * % lift
 * Date
 * % current segment
 * % baseline segment
 */
function getLiftBaselineTooltipSections({
  trendTypeSelection,
  liftSteps,
  eventIndex,
  displayName,
  liftSegmentName,
  formattedDate,
  steps,
  liftComparisonSegment,
}: {
  trendTypeSelection: TrendChartType;
  liftSteps: BasicFunnelStep[];
  eventIndex: number;
  displayName: string;
  liftSegmentName: string;
  formattedDate: string;
  steps: BasicFunnelStep[];
  liftComparisonSegment: BasicFunnelStep[];
}) {
  const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
  const step = trendValueGetter.getStep(liftSteps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
  const liftTrendValue = trendValueGetter.getTrendValue(
    step,
    Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex,
  );
  const liftDisplayValue = `${liftTrendValue > 0 ? `+` : ``}${round(liftTrendValue * 100, 4)}%`;
  const isBaselineSegment = displayName === liftSegmentName;
  return [
    {
      rows: [
        {
          items: [
            {
              text: displayName,
              type: TooltipItemType.Strong,
            },
          ],
        },
      ],
    },
    {
      rows: [
        {
          items: [
            {
              text: formattedDate,
              type: TooltipItemType.Detail,
            },
          ],
        },
      ],
    },
    {
      rows: [
        {
          items: [
            {
              text: liftDisplayValue,
              type: TooltipItemType.Data,
              theme: liftTrendValue < 0 ? THEME.NEGATIVE : THEME.POSITIVE,
            },
          ],
        },
      ],
    },
    {
      rows: getToolTipDataRows(
        steps,
        eventIndex,
        trendTypeSelection,
        null,
        false,
        // don't show the extra segment name if this is what we're comparing against
        isBaselineSegment ? null : displayName,
      ),
    },
    ...(!isBaselineSegment // only show comparison segment if it's not the one we are comparing against
      ? [
          {
            rows: getToolTipDataRows(
              liftComparisonSegment,
              eventIndex,
              trendTypeSelection,
              null,
              false,
              liftSegmentName,
            ),
          },
        ]
      : []),
  ];
}
